import { DateTime } from "luxon"
import { useCallback } from "react"
import { QueryFunctionContext, useQueryClient } from "@tanstack/react-query"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { ResourceItem } from "../../../createOrderTypes"
import { ConfigQuery, fetchEventsForServiceCenter } from "../../../createOrderApi"
import { NullableCalendarEvent, TimelineCalendarEvent } from "../calendar/fullCalendarTypes"
import { isOngoing } from "../calendar/helpers"
import { useTimelineContext } from "../context/TimelineContextProvider"
import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { hasEventOverlap, toTimelineEvents } from "../helpers"
import { dropEventsFromIndex, eventIdsToPrune, mergeEventsIntoIndex } from "./eventIndex"
import { getMinAllowedEndTime } from "./helpers"

type FetchEventsForServiceCenterQueryKey = Readonly<[string, number, string, string, string]>

export type IsResourceAvailableFn = (
    newStart: string,
    newEnd: string,
    resourceIds: string[],
    excludeEventId?: string,
) => boolean

type Options = Readonly<{}>

export function useResourceHelpers({}: Options) {
    const dateTimeFactory = useDateTime()
    const { timelineResources, resourceBufferMap, eventIndexRef, sourceEventIdsRef, userId, localEventUpdateId } =
        useTimelineContext()
    const { draftEvent: localEvent, setDraftEvent, fullResources } = useResourceBookingDraft()

    const { mutateAsync: callFetchEvents } = useApiMutation(fetchEventsForServiceCenter, {})
    const queryClient = useQueryClient()

    const loadAndTransformEvents = useCallback(
        async ({ queryKey }: QueryFunctionContext<FetchEventsForServiceCenterQueryKey>) => {
            const [_queryName, userId, serviceCenterId, startDate, endDate] = queryKey

            const eventsResponse = await callFetchEvents({
                userId,
                serviceCenterId,
                startDate,
                endDate,
            })

            // Keep this a pure API -> calendar mapping of the whole day. The "hide the order that is
            // currently being edited" filter lives in createSingleDayFetcher instead: it depends on
            // localEventUpdateId, which is not part of the query key, so filtering here would make the
            // cached result outlive the order it was filtered for.
            return toTimelineEvents(eventsResponse, dateTimeFactory)
        },
        [dateTimeFactory],
    )

    const createSingleDayFetcher = useCallback(
        (serviceCenterId: string, dayOffset: number) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                try {
                    const rangeStart = dateTimeFactory.fromISO(fetchInfo.startStr).plus({ days: dayOffset })
                    const hardEnd = dateTimeFactory.fromISO(fetchInfo.endStr)
                    const rangeEnd = DateTime.min(rangeStart.plus({ days: 1 }), hardEnd)

                    const startISO = dateTimeFactory.toApiISOMinutesFormat(rangeStart)
                    const endISO = dateTimeFactory.toApiISOMinutesFormat(rangeEnd)
                    const dayISODate = rangeStart.toISODate()

                    if (!dayISODate) {
                        successCallback([])
                        return
                    }

                    const queryKey: FetchEventsForServiceCenterQueryKey = [
                        ConfigQuery.EVENTS_LIST_BY_DAY,
                        userId,
                        serviceCenterId,
                        startISO,
                        endISO,
                    ] as const

                    const result = await queryClient.fetchQuery({
                        queryKey,
                        staleTime: 60_000,
                        queryFn: loadAndTransformEvents,
                    })

                    // The order being edited is drawn as the local draft, so it must neither appear as a
                    // server event nor block itself in the overlap index. Doing it here (outside the
                    // cached query fn) keeps one cache entry per day and makes switching to another
                    // order correct immediately, without refetching.
                    const editedEventId = localEventUpdateId ? String(localEventUpdateId) : null
                    const visibleEvents = editedEventId ? result.filter((event) => event.id !== editedEventId) : result

                    if (editedEventId) {
                        // Drop a previously indexed copy — the order may have become the edit target
                        // after it was already indexed as a regular event.
                        dropEventsFromIndex(eventIndexRef.current, new Set([editedEventId]))
                    }

                    const sourceKey = `${serviceCenterId}|${dayISODate}`
                    const currentEventIds = new Set(visibleEvents.map((event) => event.id))

                    dropEventsFromIndex(
                        eventIndexRef.current,
                        eventIdsToPrune({ sourceKey, currentEventIds, sourceEventIds: sourceEventIdsRef.current }),
                    )

                    sourceEventIdsRef.current[sourceKey] = currentEventIds

                    mergeEventsIntoIndex(eventIndexRef.current, visibleEvents)

                    successCallback(visibleEvents)
                } catch (err) {
                    failureCallback(err)
                }
            },
        [queryClient, dateTimeFactory, loadAndTransformEvents, userId, localEventUpdateId],
    )

    const handleChangeLocalEvent = useCallback(
        (event: NullableCalendarEvent) => {
            if (!event) {
                setDraftEvent(null)
                return
            }

            // A meeting that has already started may not be shortened to end sooner than the minimum
            // step from now. Only an actual end change is rejected — an untouched end (e.g. while
            // toggling resources) must stay editable even when the meeting is about to finish.
            const isOngoingUpdate = !!localEventUpdateId && !!localEvent && isOngoing(localEvent, dateTimeFactory)
            const isEndChanged = !!localEvent && event.end !== localEvent.end
            if (
                isOngoingUpdate &&
                isEndChanged &&
                dateTimeFactory.fromISO(event.end) < getMinAllowedEndTime(dateTimeFactory)
            ) {
                return
            }

            const currentFullResources: ResourceItem[] = fullResources ?? []

            const fromTimeline = timelineResources
                .filter((res) => event.resourceIds.includes(res.id))
                .map((res) => ({
                    ...res,
                    id: Number(res.id),
                }))

            const fromPrevious = currentFullResources
                .filter(
                    (res) =>
                        event.resourceIds.includes(res.id.toString()) && !fromTimeline.some((r) => r.id === res.id),
                )
                .map((res) => ({
                    ...res,
                    id: Number(res.id),
                }))

            setDraftEvent(event, [...fromTimeline, ...fromPrevious])
        },
        [dateTimeFactory, fullResources, localEvent, localEventUpdateId, timelineResources, setDraftEvent],
    )

    const checkOverlap = useCallback(
        (newStart: string, newEnd: string, resourceId: string, excludeEventId?: string): boolean => {
            const relevantEvents = eventIndexRef.current[resourceId] || []
            const allEvents = [...relevantEvents, ...(localEvent?.resourceIds.includes(resourceId) ? [localEvent] : [])]

            return hasEventOverlap({
                start: newStart,
                end: newEnd,
                buffer: resourceBufferMap[resourceId] || 0,
                events: allEvents,
                dateTimeFactory,
                excludeEventId,
            })
        },
        [resourceBufferMap, dateTimeFactory],
    )

    const isResourceAvailable: IsResourceAvailableFn = useCallback(
        (newStart, newEnd, resourceIds, excludeEventId) => {
            return !resourceIds.some((resourceId) => checkOverlap(newStart, newEnd, resourceId, excludeEventId))
        },
        [checkOverlap],
    )

    return {
        createSingleDayFetcher,
        handleChangeLocalEvent,
        isResourceAvailable,
    }
}
